import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getAllPlays, updatePlay, uploadPlayCoverImage, deletePlayCoverImage } from '../../../services/playsService';

const PlaysEditor = ({ userId }) => {
  const [plays, setPlays] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ 
    title: '', 
    author: '', 
    description: '', 
    coverImageUrl: '',
    yearWritten: '',
    difficulty: 'medium'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadPlays();
  }, []);

  const loadPlays = async () => {
    try {
      const data = await getAllPlays();
      setPlays(data || []);
    } catch (error) {
      console.error('Error loading plays:', error);
      setError('Failed to load plays');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (play) => {
    setEditingId(play?.id);
    setEditForm({
      title: play?.title || '',
      author: play?.author || '',
      description: play?.description || '',
      coverImageUrl: play?.cover_image_url || '',
      yearWritten: play?.year_written || '',
      difficulty: play?.difficulty || 'medium'
    });
    setImagePreview(play?.cover_image_url || null);
    setImageFile(null);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({ 
      title: '', 
      author: '', 
      description: '', 
      coverImageUrl: '',
      yearWritten: '',
      difficulty: 'medium'
    });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file?.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader?.result);
    };
    reader?.readAsDataURL(file);
    setError(null);
  };

  const handleSave = async (playId) => {
    if (!editForm?.title?.trim() || !editForm?.author?.trim()) {
      setError('Title and author are required');
      return;
    }

    try {
      setUploadingImage(true);
      let coverImageUrl = editForm?.coverImageUrl;

      // Upload new image if selected
      if (imageFile) {
        // Delete old image if exists
        if (editForm?.coverImageUrl) {
          try {
            await deletePlayCoverImage(editForm?.coverImageUrl);
          } catch (deleteError) {
            console.error('Error deleting old image:', deleteError);
            // Continue even if delete fails
          }
        }

        // Upload new image
        coverImageUrl = await uploadPlayCoverImage(imageFile, playId);
      }

      // Update play in database
      const updates = {
        title: editForm?.title?.trim(),
        author: editForm?.author?.trim(),
        description: editForm?.description?.trim() || null,
        cover_image_url: coverImageUrl || null,
        year_written: editForm?.yearWritten ? parseInt(editForm?.yearWritten) : null,
        difficulty: editForm?.difficulty
      };

      await updatePlay(playId, updates, userId);

      setSuccess('Play updated successfully');
      setEditingId(null);
      setEditForm({ 
        title: '', 
        author: '', 
        description: '', 
        coverImageUrl: '',
        yearWritten: '',
        difficulty: 'medium'
      });
      setImageFile(null);
      setImagePreview(null);
      loadPlays();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Update error:', error);
      setError(`Failed to update: ${error?.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const getDifficultyBadgeColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'hard':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="modern-card">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
          <p className="text-muted-foreground mt-2">Loading plays...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Icon name="BookOpen" size={24} className="text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Greatest Hits - Play Management</h2>
          <p className="text-sm text-muted-foreground">
            Edit play information and images displayed on the homepage
          </p>
        </div>
      </div>
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={18} className="text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Icon name="CheckCircle" size={18} className="text-green-600" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}
      {/* Plays List */}
      <div className="space-y-6">
        {plays?.map((play) => (
          <div key={play?.id} className="border border-border rounded-lg p-6">
            {editingId === play?.id ? (
              // Edit Mode
              (<div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Play Title *
                    </label>
                    <input
                      type="text"
                      value={editForm?.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e?.target?.value })}
                      className="input-field"
                      placeholder="Enter play title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Author *
                    </label>
                    <input
                      type="text"
                      value={editForm?.author}
                      onChange={(e) => setEditForm({ ...editForm, author: e?.target?.value })}
                      className="input-field"
                      placeholder="Enter author name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Year Written
                    </label>
                    <input
                      type="number"
                      value={editForm?.yearWritten}
                      onChange={(e) => setEditForm({ ...editForm, yearWritten: e?.target?.value })}
                      className="input-field"
                      placeholder="e.g., 1595"
                      min="1500"
                      max="1616"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Difficulty Level
                    </label>
                    <select
                      value={editForm?.difficulty}
                      onChange={(e) => setEditForm({ ...editForm, difficulty: e?.target?.value })}
                      className="input-field"
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm?.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
                    rows={3}
                    className="input-field resize-none"
                    placeholder="Enter a brief description of the play"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {editForm?.description?.length} characters
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cover Image
                  </label>
                  <div className="space-y-3">
                    {imagePreview && (
                      <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageChange}
                      className="input-field"
                      disabled={uploadingImage}
                    />
                    <p className="text-xs text-muted-foreground">
                      Accepted formats: JPEG, PNG, WebP (max 10MB)
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleSave(play?.id)}
                    disabled={uploadingImage}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingImage ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Icon name="Check" size={18} />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={uploadingImage}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon name="X" size={18} />
                    Cancel
                  </button>
                </div>
              </div>)
            ) : (
              // View Mode
              (<div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    {play?.cover_image_url && (
                      <div className="relative w-32 h-48 rounded-lg overflow-hidden border-2 border-border flex-shrink-0">
                        <img
                          src={play?.cover_image_url}
                          alt={`${play?.title} cover`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-foreground">{play?.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getDifficultyBadgeColor(play?.difficulty)}`}>
                          {play?.difficulty || 'medium'}
                        </span>
                      </div>
                      <p className="text-sm text-purple-400 font-medium mb-2">
                        {play?.author}{play?.year_written ? `, ${play?.year_written}` : ''}
                      </p>
                      {play?.description && (
                        <p className="text-muted-foreground leading-relaxed text-sm">
                          {play?.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(play)}
                    className="btn-secondary flex-shrink-0"
                  >
                    <Icon name="Edit2" size={18} />
                    Edit
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Book" size={14} />
                    <span>{play?.total_acts || 0} Acts</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={14} />
                    <span>{play?.total_scenes || 0} Scenes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="MessageSquare" size={14} />
                    <span>{play?.total_lines || 0} Lines</span>
                  </div>
                  {play?.is_published && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      Published
                    </span>
                  )}
                </div>
              </div>)
            )}
          </div>
        ))}
      </div>
      {plays?.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="BookOpen" size={48} className="mx-auto mb-3 opacity-30" />
          <p>No plays found</p>
        </div>
      )}
      {/* Instructions */}
      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={18} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Guidelines:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Use high-quality cover images (recommended: 800x1200px or similar portrait ratio)</li>
              <li>Keep descriptions concise and engaging (1-2 sentences)</li>
              <li>Ensure all plays have consistent image quality and style</li>
              <li>Changes are immediately visible on the homepage Greatest Hits section</li>
              <li>Images are optimized and cached for fast loading</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaysEditor;